import * as z from 'zod';

import { LosId } from '../../BrandedIds';
import { add } from '../../utils/math';
import { DateString, NonNegativeMonetaryValue } from '../fields';

const baseShape = {
  loanId: LosId,
  disbursementType: z.enum(['escrow']),
  disbursementDate: DateString,
  disbursementTotal: NonNegativeMonetaryValue,
  escrowAmount: NonNegativeMonetaryValue.optional(),
  suspenseAmount: NonNegativeMonetaryValue.optional(),
  unappliedSuspenseAmount: NonNegativeMonetaryValue.optional(),
  holdbackAmount: NonNegativeMonetaryValue.optional(),
  lossDraftAmount: NonNegativeMonetaryValue.optional(),
  principalPrepaymentAmount: NonNegativeMonetaryValue.optional(),
  payeeId: z.string().optional(),
  disbursementRecipient: z.string().optional(),
  policyNumber: z.string().optional(),
  updateNextPaymentAmount: z.boolean().optional(),
  note: z.string().optional(),
};

const baseObject = z.object(baseShape);

export const ESCROW_DISBURSEMENT_AMOUNT_FIELDS = [
  'escrowAmount',
  'suspenseAmount',
  'unappliedSuspenseAmount',
  'holdbackAmount',
  'lossDraftAmount',
  'principalPrepaymentAmount',
] as const;

type EscrowDisbursementBuckets = { [K in (typeof ESCROW_DISBURSEMENT_AMOUNT_FIELDS)[number]]?: number };

export const hasEscrowDisbursementBuckets = (row: EscrowDisbursementBuckets): boolean =>
  ESCROW_DISBURSEMENT_AMOUNT_FIELDS.some((fieldName) => (row[fieldName] ?? 0) > 0);

type EscrowDisbursementRefineInput = {
  disbursementTotal: number;
  payeeId?: string;
  disbursementRecipient?: string;
} & EscrowDisbursementBuckets;

const refineEscrowDisbursementRow = (row: EscrowDisbursementRefineInput, ctx: z.RefinementCtx) => {
  if (!row.payeeId && !row.disbursementRecipient) {
    ctx.addIssue({
      code: 'custom',
      path: ['disbursementRecipient'],
      message: 'Either payeeId or disbursementRecipient is required.',
    });
  }

  if (hasEscrowDisbursementBuckets(row)) {
    const bucketTotal = add(...ESCROW_DISBURSEMENT_AMOUNT_FIELDS.map((fieldName) => row[fieldName] ?? 0));
    if (bucketTotal !== row.disbursementTotal) {
      ctx.addIssue({
        code: 'custom',
        path: ['disbursementTotal'],
        message: 'The disbursement amounts (escrow, suspense, holdback, etc.) must sum to disbursementTotal.',
      });
    }
  }
};

export const RecordEscrowDisbursementRow = baseObject.superRefine(refineEscrowDisbursementRow);
export type RecordEscrowDisbursementRow = z.infer<typeof RecordEscrowDisbursementRow>;

export const RecordEscrowDisbursementApiRow = baseObject
  .omit({ disbursementType: true })
  .superRefine(refineEscrowDisbursementRow);
export type RecordEscrowDisbursementApiRow = z.infer<typeof RecordEscrowDisbursementApiRow>;

export const RECORD_ESCROW_DISBURSEMENT_ROW_REQUIRED_FIELDS = Object.keys(baseShape).filter(
  (key) => !baseShape[key as keyof typeof baseShape].isOptional(),
);
